import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToolFormModel } from '../../tool-create.model';
import {
  CODING_TYPE_NA_OPTION,
  CODING_TYPE_OPTIONS,
  CodingTypeOption,
  CodingTypeValue,
  isAutoGeneratedToolCodeCodingType,
  isNACodingType,
  normalizeCodingTypeValue
} from '@app/shared/models/enums/codingType.enum';

import {
  ToolService
} from '@app/core/services/tool.service';
import { ToolPropertyValidationState } from '@app/tool/model/tool-property-validation-state.enum';
import { ToolValidationConflictTool } from '@app/tool/model/tool-validation-conflict-tool.model';
import { ToolPropertyValidationResult } from '@app/tool/model/tool-property-validation-result.model';
import { debounceTime, switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { merge } from 'rxjs';
import { ToolProperty } from '@app/tool/model/enums/tool-property.enum';
import { ToolPropertyValidationInfoModalComponent } from '@app/tool/components/tool-property-validation-info-modal/tool-property-validation-info-modal.component';

@Component({
  selector: 'app-toolset-codes-form',
  templateUrl: './toolset-codes-form.component.html',
  styleUrls: ['./toolset-codes-form.component.scss']
})
export class ToolsetCodesFormComponent implements OnInit, OnChanges {
  @ViewChild('infoModal', { static: true }) infoModal: ToolPropertyValidationInfoModalComponent;

  @Input() tool: ToolFormModel = new ToolFormModel();
  @Input() showValidations = false;
  @Input() toolId: number = null;
  @Input() isLoading = false;

  forms: UntypedFormGroup[] = [];
  initialized = false;
  infoModalTools: ToolValidationConflictTool[] = [];
  private acknowledgedInfoSignatures = new Set<string>();
  private pendingInfoContext: { form: UntypedFormGroup, signature: string } = null;

  codingTypeOptions: CodingTypeOption[] = CODING_TYPE_OPTIONS;
  constructor(private toolService: ToolService) {}

  ngOnInit(): void {
    this.initializeForms();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tool'] && !changes['tool'].firstChange) {
      this.revalidateForms();
    }
  }

  public initializeForms() {
    this.forms = [];
    const nrTools = this.tool.isSet ? this.tool.setSize : 1;

    for (let i = 0; i < nrTools; i++) {
      const codingType = normalizeCodingTypeValue(this.tool.codingTypes[i]);
      const selectedCodingType = this.getCodingTypeOption(codingType);
      const initialMesCode = isNACodingType(selectedCodingType.value)
        ? 'N/A'
        : isAutoGeneratedToolCodeCodingType(selectedCodingType.value)
          ? ''
        : this.tool.mesCodes[i];

      const form = new UntypedFormGroup({
        mesCode: new UntypedFormControl(initialMesCode),
        supplierCode: new UntypedFormControl(this.tool.supplierCodes[i]),
        inventoryNumber: new UntypedFormControl(this.tool.inventoryNumbers[i]),
        toolControlName: new UntypedFormControl(this.tool.toolControlNames[i]),
        codingType: new UntypedFormControl(selectedCodingType),
      });

      this.forms.push(form);
      this.bindToolCodeValidation(form);
    }

    this.initialized = true;
    this.revalidateForms();
  }

  get allFormsValid(): boolean {
    return this.forms.every(f => f.valid && !f.pending);
  }

  getFormValue() {
    const formsValue = {
      mesCodes: [],
      supplierCodes: [],
      inventoryNumbers: [],
      toolControlNames: [],
      codingTypes: [],
    };

    this.forms.forEach((form) => {
      const value = form.value;
      formsValue.mesCodes.push(this.getSubmittedToolCode(form));
      formsValue.supplierCodes.push(value.supplierCode);
      formsValue.inventoryNumbers.push(value.inventoryNumber);
      formsValue.toolControlNames.push(value.toolControlName);

      const rawValue = value.codingType?.value ?? value.codingType;
      formsValue.codingTypes.push(normalizeCodingTypeValue(rawValue));
    });

    return formsValue;
  }


  getSetSize(): number {
    return this.initialized ? this.forms.length : 0;
  }

  resetForm() {
    this.forms.forEach( (form) => form.reset() );
    this.initialized = false;
  }

  get isInitialized(): boolean {
    return this.initialized;
  }

  revalidate() {
    this.revalidateForms();
  }

  canReopenInfoModal(form: UntypedFormGroup): boolean {
    return !!form.get('mesCode')?.errors?.toolCodeInfoPending && !!this.infoModalTools.length;
  }

  isToolCodeReadonly(form: UntypedFormGroup): boolean {
    return isNACodingType(this.getCodingTypeValue(form)) || this.isToolCodeAutoGenerated(form);
  }

  isToolCodeAutoGenerated(form: UntypedFormGroup): boolean {
    return isAutoGeneratedToolCodeCodingType(this.getCodingTypeValue(form));
  }

  reopenInfoModal(form: UntypedFormGroup) {
    if (!this.canReopenInfoModal(form)) {
      return;
    }

    this.pendingInfoContext = {
      form,
      signature: this.buildValidationSignature(form)
    };
    this.infoModal.open();
  }

  private bindToolCodeValidation(form: UntypedFormGroup) {
    const mesCodeCtrl = form.get('mesCode')!;
    const codingTypeCtrl = form.get('codingType')!;

    codingTypeCtrl.valueChanges.subscribe(() => {
      this.syncToolCodeWithCodingType(form);
    });

    merge(
      mesCodeCtrl.valueChanges.pipe(
        debounceTime(500)
      ),
      codingTypeCtrl.valueChanges
    )
      .pipe(
        tap(() => this.resetInfoAcknowledgement(form)),
        switchMap(() => this.validateToolCode(form))
      )
      .subscribe((isValid) => {
        this.applyServerValidationResult(form, isValid);
      });
  }

  private validateToolCode(form: UntypedFormGroup) {
    const mesCodeCtrl = form.get('mesCode')!;
    const trimmedCode = this.normalizeCode(mesCodeCtrl.value);
    const codingType = this.getCodingTypeValue(form);
    const equipmentIds = this.getRelatedEquipmentIds();

    this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
    this.removeError(mesCodeCtrl, 'toolCodeInfoPending');

    if (!trimmedCode || isNACodingType(codingType) || isAutoGeneratedToolCodeCodingType(codingType) || !equipmentIds.length) {
      return of(null);
    }

    return this.toolService.validateToolProperty(ToolProperty.MESCode, trimmedCode, {
      codingType,
      equipmentIds,
      toolId: this.toolId
    });
  }

  private applyServerValidationResult(form: UntypedFormGroup, result: ToolPropertyValidationResult | null) {
    const mesCodeCtrl = form.get('mesCode')!;
    const signature = this.buildValidationSignature(form);

    if (result === null) {
      return;
    }

    switch (result.validationState) {
      case ToolPropertyValidationState.Valid:
        this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
        this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
        return;
      case ToolPropertyValidationState.Info:
        if (this.acknowledgedInfoSignatures.has(signature)) {
          this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
          return;
        }
        this.setError(mesCodeCtrl, 'toolCodeInfoPending');
        this.infoModalTools = result.tools || [];
        this.pendingInfoContext = { form, signature };
        this.infoModal.open();
        return;
      case ToolPropertyValidationState.Error:
        this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
        this.setError(mesCodeCtrl, 'toolCodeNotUnique', {
          tools: this.buildToolsLabel(result.tools || [])
        });
        return;
      default:
        this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
    }
  }

  private revalidateForms() {
    this.forms.forEach(form => {
      this.validateToolCode(form).subscribe((result) => {
        this.applyServerValidationResult(form, result);
      });
    });
  }

  private getRelatedEquipmentIds(): number[] {
    const equipments = [
      ...(this.tool?.mainEquipment || []),
      ...(this.tool?.secondaryEquipment || [])
    ];

    return equipments
      .map(equipment => equipment?.id)
      .filter(id => !!id);
  }

  private getCodingTypeValue(form: UntypedFormGroup): CodingTypeValue {
    const rawValue = form.get('codingType')?.value?.value ?? form.get('codingType')?.value ?? 'N/A';
    return normalizeCodingTypeValue(rawValue);
  }

  private getCodingTypeOption(value: CodingTypeValue): CodingTypeOption {
    return this.codingTypeOptions.find(opt => opt.value === value) || CODING_TYPE_NA_OPTION;
  }

  private syncToolCodeWithCodingType(form: UntypedFormGroup) {
    const mesCodeCtrl = form.get('mesCode')!;
    const codingType = this.getCodingTypeValue(form);
    const isNA = isNACodingType(codingType);
    const isAutoGenerated = isAutoGeneratedToolCodeCodingType(codingType);

    if (isNA) {
      mesCodeCtrl.setValue('N/A', { emitEvent: false });
      this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
      this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
      return;
    }

    if (isAutoGenerated) {
      mesCodeCtrl.setValue('', { emitEvent: false });
      this.removeError(mesCodeCtrl, 'toolCodeNotUnique');
      this.removeError(mesCodeCtrl, 'toolCodeInfoPending');
      return;
    }

    if (this.normalizeCode(mesCodeCtrl.value) === 'N/A') {
      mesCodeCtrl.setValue('', { emitEvent: false });
    }
  }

  private getSubmittedToolCode(form: UntypedFormGroup): string | null {
    const codingType = this.getCodingTypeValue(form);

    if (isAutoGeneratedToolCodeCodingType(codingType)) {
      return null;
    }

    return form.value.mesCode;
  }

  private normalizeCode(value: string): string {
    return value?.trim() || '';
  }

  onInfoConfirm() {
    if (!this.pendingInfoContext) {
      return;
    }

    this.acknowledgedInfoSignatures.add(this.pendingInfoContext.signature);
    this.removeError(this.pendingInfoContext.form.get('mesCode')!, 'toolCodeInfoPending');
    this.pendingInfoContext = null;
    this.infoModalTools = [];
  }

  private resetInfoAcknowledgement(form: UntypedFormGroup) {
    this.acknowledgedInfoSignatures.clear();
    this.removeError(form.get('mesCode')!, 'toolCodeInfoPending');

    if (this.pendingInfoContext?.form === form) {
      this.pendingInfoContext = null;
      this.infoModalTools = [];
    }
  }

  private buildValidationSignature(form: UntypedFormGroup): string {
    return JSON.stringify({
      mesCode: this.normalizeCode(form.get('mesCode')?.value),
      codingType: this.getCodingTypeValue(form),
      equipmentIds: this.getRelatedEquipmentIds(),
      toolId: this.toolId
    });
  }

  private buildToolsLabel(tools: ToolValidationConflictTool[]): string {
    return tools
      .map(tool => `${tool.toolUid} - ${tool.name}`)
      .join(', ');
  }

  private setError(control: AbstractControl, key: string, value: any = true) {
    control.setErrors({
      ...(control.errors || {}),
      [key]: value
    });
  }

  private removeError(control: AbstractControl, key: string) {
    const errors = { ...(control.errors || {}) };
    delete errors[key];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

}
